import React, { useEffect, useMemo, useState } from 'react';
import CategoryTree from '../../../components/global/CategoryTree/CategoryTree';
import { setSubpage } from '../../../routes/routerSlice';
import { ConfigurationSubpage } from '../Configuration';
import CategoryDrillDown from './CategoryDrillDown/CategoryDrillDown';
import { MainContent, RowCentered, Section } from '@ydistri/ds';
import StoreDrillDown from './StoreDrillDown/StoreDrillDown';
import { useSearchParams } from 'react-router';
import { DRILL_DOWN_TYPE_URL_PARAM, SkuTypeForDrilldown } from './drillDownLib';
import { CalculationDetailSubpages } from '../../Calculations/calculationsLib';
import { SkuTypeButton } from './SkuTypeButton';
import AlertOutdated from '../../../components/global/AlertOutdated/AlertOutdated';
import { useGetCalculationQuery } from '../../CalculationDetail/apiCalculationDetail';
import { CalculationMode } from '@ydistri/api-sdk';
import { useAppDispatch } from '../../../store';
import { CategoryTreePresentationMode } from '../../../components/global/CategoryTree/CategoryTreeTypes';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';

const drillDownSkuTypes: SkuTypeForDrilldown[] = ['total', 'DeadStock', 'SlowMover', 'FastMover'];

const ConfigurationDrillDown: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: calculationData } = useGetCalculationQuery(
    templateOrCalculation.type === 'Calculation' && templateOrCalculation.hasValidValue
      ? templateOrCalculation.id
      : skipToken,
  );
  const [selectedSkuType, setSelectedSkuType] = useState<SkuTypeForDrilldown>('total');

  // Only non-Computed calculations use total attribute values instead of potential summaries.
  const isNonComputedCalculation =
    templateOrCalculation.type === 'Calculation' &&
    calculationData !== undefined &&
    calculationData.mode !== CalculationMode.Computed;
  const takeSupplyAndRevenueFromAttributes = isNonComputedCalculation;
  const omitPotentialColumns = isNonComputedCalculation;

  const drillDownType = searchParams.get(DRILL_DOWN_TYPE_URL_PARAM) ?? 'categories';

  useEffect(() => {
    dispatch(
      setSubpage(
        templateOrCalculation.type === 'Calculation'
          ? CalculationDetailSubpages.DRILLDOWN
          : ConfigurationSubpage.DRILLDOWN,
      ),
    );
  }, [dispatch, templateOrCalculation.type]);

  const headerActions = useMemo(() => {
    if (isNonComputedCalculation) return null;
    return (
      <RowCentered $gap="2rem">
        {drillDownType === 'categories' && (
          <RowCentered $gap="0.5rem">
            {drillDownSkuTypes.map(drillDownSkuType => (
              <SkuTypeButton
                skuType={drillDownSkuType}
                selected={selectedSkuType === drillDownSkuType}
                onSkuTypeClick={setSelectedSkuType}
                key={drillDownSkuType}
              />
            ))}
          </RowCentered>
        )}
      </RowCentered>
    );
  }, [drillDownType, isNonComputedCalculation, selectedSkuType]);

  return (
    <>
      {drillDownType === 'stores' && (
        <CategoryTree presentationMode={CategoryTreePresentationMode.DISPLAY} />
      )}
      <MainContent>
        <Section header="Drill down" headerActions={headerActions}>
          <AlertOutdated />
          {drillDownType === 'categories' && (
            <CategoryDrillDown
              selectedSkuType={selectedSkuType}
              takeSupplyAndRevenueFromAttributes={takeSupplyAndRevenueFromAttributes}
              omitPotentialColumns={omitPotentialColumns}
            />
          )}
          {drillDownType === 'stores' && <StoreDrillDown />}
        </Section>
      </MainContent>
    </>
  );
};

export default ConfigurationDrillDown;
